import { useSelector } from "react-redux"
import { SpinnerText } from "../../ui/Spinner"
import { RootState } from "../../../store/store"
import { resetForm } from "../../../hooks/resetForm"
import { saveDraft } from "../../../services/saveDraftService"
import { updateApplicationStatus } from "../../../services/applicationService"
import { validateApplication } from "../../../services/euipoApplicationSandboxService"
import { MarkData } from "../mark/MarkSection"
import { formatApiError } from "../../../utils/formatApiError"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { FiCreditCard, FiRefreshCw, FiSave } from "react-icons/fi"
import StatusToast from "../../ui/StatusToast"
import StatusBadge from "../../ui/StatusBadge"

interface GoodsAndServicesUiEntry {
  classNumber: number
  text: string
}

interface ActionSectionProps {
  applicationStatus?: string | null
  referenceNumber: string
  markData: MarkData | null
  goodsAndServices?: GoodsAndServicesUiEntry[] | null
  applicants?: any[] | null
  priorityData?: any[] | null
  seniorityData?: any[] | null
  signature?: any | null
  draftId?: string | null
  markId?: string | null
  goodsAndServicesId?: string | null
  signatureId?: string | null
  onDraftSaved?: (id: string) => void
  onMarkSaved?: (id: string | null) => void
  onGoodsAndServicesSaved?: (id: string | null) => void
  onPrioritiesSaved?: (ids: string[] | null) => void
  onSenioritiesSaved?: (ids: string[] | null) => void
  onSignatureSaved?: (id: string | null) => void
  onValidationError?: (error: unknown) => void
  paymentEnabled?: boolean
}

const FIXED_LANGUAGES = {
  filingLanguage: "English",
  secondLanguage: "German"
}

export default function ActionSection(props: ActionSectionProps) {
  const navigate = useNavigate()
  const token = useSelector((state: RootState) => state.auth.token)
  const isLocked =
    props.applicationStatus === "PAID" ||
    props.applicationStatus === "PAYMENT_PENDING" ||
    props.applicationStatus === "AWAITING_RELEASE"
  const [message, setMessage] = useState<{
    type: "success" | "error"
    text: string
  } | null>(null)
  const [saving, setSaving] = useState(false)

  const handleSaveDraft = async () => {
    if (!token || isLocked || saving) return

    try {
      setSaving(true)
      await saveDraft({
        token,
        referenceNumber: props.referenceNumber,
        languages: FIXED_LANGUAGES,
        markData: props.markData,
        goodsAndServices: props.goodsAndServices,
        applicants: props.applicants,
        priorityData: props.priorityData,
        seniorityData: props.seniorityData,
        signature: props.signature,
        draftId: props.draftId,
        markId: props.markId,
        goodsAndServicesId: props.goodsAndServicesId,
        signatureId: props.signatureId,
        onDraftSaved: props.onDraftSaved,
        onMarkSaved: props.onMarkSaved,
        onGoodsAndServicesSaved: props.onGoodsAndServicesSaved,
        onPrioritiesSaved: props.onPrioritiesSaved,
        onSenioritiesSaved: props.onSenioritiesSaved,
        onSignatureSaved: props.onSignatureSaved
      })

      setMessage({
        type: "success",
        text: "Draft saved successfully."
      })
    } catch (error) {
      setMessage({
        type: "error",
        text: formatApiError(
          error,
          "The draft could not be saved. Please reload the page and try again."
        )
      })
    } finally {
      setSaving(false)
    }
  }

  const handleProceedToPayment = async () => {
    if (!token || isLocked || saving || !props.paymentEnabled) return

    try {
      setSaving(true)
      const id = await saveDraft({
        token,
        referenceNumber: props.referenceNumber,
        languages: FIXED_LANGUAGES,
        markData: props.markData,
        goodsAndServices: props.goodsAndServices,
        applicants: props.applicants,
        priorityData: props.priorityData,
        seniorityData: props.seniorityData,
        signature: props.signature,
        draftId: props.draftId,
        markId: props.markId,
        goodsAndServicesId: props.goodsAndServicesId,
        signatureId: props.signatureId,
        onDraftSaved: props.onDraftSaved,
        onMarkSaved: props.onMarkSaved,
        onGoodsAndServicesSaved: props.onGoodsAndServicesSaved,
        onPrioritiesSaved: props.onPrioritiesSaved,
        onSenioritiesSaved: props.onSenioritiesSaved,
        onSignatureSaved: props.onSignatureSaved
      })

      try {
        await validateApplication(id)
      } catch (error) {
        props.onValidationError?.(error)
        throw error
      }
      await updateApplicationStatus(id, "VALIDATED")
      navigate(`/applications/${id}/payment`)
    } catch (error) {
      setMessage({
        type: "error",
        text: formatApiError(
          error,
          "The application could not be saved. Please reload the page and try again."
        )
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-2 rounded-md border border-gray-200 bg-white p-3 text-sm shadow-sm mt-4">
      {message && (
        <StatusToast
          type={message.type}
          message={message.text}
          onClose={() => setMessage(null)}
        />
      )}

      {saving && (
        <StatusToast type="loading" message="Saving your trademark application…" />
      )}

      <div className="mb-1 flex items-center justify-between gap-3 border-b border-gray-100 pb-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Status
        </span>
        <StatusBadge status={props.applicationStatus || "DRAFT"} />
      </div>

      <button
        onClick={handleSaveDraft}
        disabled={isLocked || saving}
        className={`flex w-full items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
          isLocked || saving
            ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400"
            : "border-gray-300 bg-white text-dantzBlue hover:border-dantzBlue hover:bg-blue-50"
        }`}
      >
        <FiSave className="h-4 w-4" aria-hidden="true" />
        {saving ? <SpinnerText>Saving draft...</SpinnerText> : "Save as draft"}
      </button>

      <button
        onClick={() => {
          if (isLocked || saving) return
          if (resetForm) resetForm()
          window.dispatchEvent(new CustomEvent("resetAll"))
        }}
        disabled={isLocked || saving}
        className={`flex w-full items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
          isLocked || saving
            ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400"
            : "border-gray-300 bg-white text-dantzBlue hover:border-dantzBlue hover:bg-blue-50"
        }`}
      >
        <FiRefreshCw className="h-4 w-4" aria-hidden="true" />
        Reset form
      </button>

      <button
        onClick={handleProceedToPayment}
        disabled={isLocked || saving || !props.paymentEnabled}
        className={`flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
          props.paymentEnabled && !isLocked && !saving
            ? "bg-dantzOrange text-white hover:brightness-110 active:scale-[0.98]"
            : "cursor-not-allowed bg-gray-300 text-gray-500"
        }`}
      >
        <FiCreditCard className="h-4 w-4" aria-hidden="true" />
        Proceed to payment
      </button>

      {isLocked && (
        <p className="text-xs text-gray-500 mt-2">
          This application has already been submitted and can no longer be edited.
        </p>
      )}

    </div>
  )
}
